import { UserActions } from 'BMapsCmds';
import { FoldingDataImportCase } from 'BMapsSrc/model/MapCase';
import { PropApplicability } from 'BMapsSrc/model/ScopedPropertyGroup';
import { deepFreeze } from 'BMapsUtil/js_utils';
import { JobDefinition, JobResultDestinations, JobTypes } from '../JobDefinition';

const scopedPropertyMetadata = deepFreeze({
    compound: {
        childGroups: {
            boltz: {
                metadata: {
                    label: 'Boltz properties',
                    tooltipHeading: 'Boltz properties',
                },
                properties: {
                    binding_affinity: {
                        metadata: {
                            label: 'Pred. Affinity (Boltz-2)',
                            propApplicability: PropApplicability.PERCOMPOUNDTARGET,
                            description: "Boltz-2's predicted affinity (log(IC50) μM) of an active ligand to the target, from the ensemble model. Suggested for hit-to-lead or lead-optimization.",
                        },
                    },
                    binding_probability: {
                        metadata: {
                            label: 'Pred. Probability (Boltz-2)',
                            propApplicability: PropApplicability.PERCOMPOUNDTARGET,
                            description: "Boltz-2's predicted probability that a ligand binds to the target. Suggested for hit identification.",
                        },
                    },
                },
            },
        },
    },
    target: {
        childGroups: {
            boltz: {
                metadata: {
                    label: 'Boltz properties',
                    tooltipHeading: 'Boltz properties',
                },
                properties: {
                    folding_confidence: {
                        metadata: {
                            label: 'Folding Confidence (Boltz)',
                            description: "Boltz's confidence in the folding results.",
                        },
                    },
                    other_affinity_binder: {
                        metadata: {
                            label: 'Other Affinity Binder (Boltz-2)',
                            description: 'The chain id of the non-compound molecule used for Boltz-2 affinity prediction.',
                        },
                    },
                    other_binding_affinity: {
                        metadata: {
                            label: 'Other Pred. Affinity (Boltz-2)',
                            description: "Boltz-2's predicted affinity of the non-compound binder to the target - log(IC50) μM",
                        },
                    },
                    other_binding_probability: {
                        metadata: {
                            label: 'Other Pred. Probability (Boltz-2)',
                            description: "Boltz-2's predicted probability that the non-compound binder binds to the target.",
                        },
                    },
                },
            },
        },
    },
});

/**
 * Protein folding job: runs a folding program and loads the resulting
 * structure into BMaps as a new map case.
 *
 * @augments JobDefinition
 */
export class FoldingJob extends JobDefinition {
    static jobTypeId = JobTypes.FOLDING;
    static resultDestination = JobResultDestinations.STRUCTURE;
    static jobTypeName = 'Folding';
    static jobName = 'Folding Job';

    static scopedPropertyMetadata = scopedPropertyMetadata;

    /**
     * Adds the molecule name and folding program to the request metadata.
     * @override
     * @param {object} jobrequest
     * @param {object} [stash]
     * @returns {object}
     */
    getRequestMeta(jobrequest, stash = {}) {
        return {
            moleculeName: stash?.mapCaseParams?.name || undefined,
            foldingProgram: jobrequest.foldingProgramId,
        };
    }

    /**
     * Parses the folding results and loads the first produced structure into
     * the workspace via `UserActions.ChooseProtein`.
     * @override
     * @param {object} job
     * @param {object|string} results
     * @param {import('../JobDefinition').IntegrateResultsOptions} [options]
     * @returns {Promise<import('../JobDefinition').IntegrateResultsReturn>}
     */
    async integrateResults(job, results, options) {
        let resultsPacket = results;
        let error;
        try {
            if (typeof resultsPacket === 'string') resultsPacket = JSON.parse(resultsPacket);
            if (!resultsPacket) {
                return { error: 'Empty results' };
            }
            let jobResults = resultsPacket.jobResults;
            if (typeof jobResults === 'string') {
                // For now, we expect that the results are already an object.
                // But parsing here let's us load job results from before this change.
                jobResults = JSON.parse(jobResults);
            }
            const firstResult = jobResults?.find((r) => r.data);
            if (!firstResult) {
                return { error: 'Empty results' };
            }
            const stash = resultsPacket?.stash;
            const loadOptions = { ...stash?.loadOptions, ...options?.loadOptions };
            const mapCase = new FoldingDataImportCase({
                data: firstResult.data,
                format: firstResult.format,
                foldingData: {
                    foldingProgramId: resultsPacket.jobrequest.foldingProgramId,
                    foldingArgs: resultsPacket.jobrequest,
                },
                resultProperties: firstResult.resultProperties,
                loadOptions,
                ...stash?.mapCaseParams,
            });
            const { errors } = await UserActions.ChooseProtein(
                mapCase, loadOptions, options?.dataConnection
            );
            if (errors.length > 0) {
                error = `Failed to load the folding results into BMaps: ${errors.join('; ')}`;
            }
        } catch (ex) {
            error = 'Error loading folding results';
            console.error(error, ex);
        }
        return { error, results: resultsPacket };
    }
}
